/* eslint-disable max-lines */
/* eslint-disable no-console */
// Inspired by: https://dev.to/aduranil/how-to-use-websockets-with-redux-a-step-by-step-guide-to-writing-understanding-connecting-socket-middleware-to-your-project-km3
import { datadogLogs } from '@datadog/browser-logs';
import {
    type Action,
    type MiddlewareAPI,
    type ThunkAction,
    type ThunkDispatch,
    type ThunkMiddleware,
    type UnknownAction,
} from '@reduxjs/toolkit';
import dayjs from 'dayjs';

import { type Conversation } from 'api/userSupportAPI';
import { getSocketURL, type SocketType, SocketTypes } from 'services/socketHelpers';
import {
    type ConversationParticipation,
    setUserSupportConversation,
    setUserSupportReferral,
    SocketMessageTypes,
} from 'services/userSupportHelpers';
import { type authSlice, type AuthSliceState } from 'store/auth/authSlice';
import { type SocketClaim, updateClaimsData } from 'store/claims/claimsListSlice';
import { type SocketLiveReferral, updateAuctions } from 'store/liveReferrals/liveReferralsSlice';
import { type SocketBidsPayload, updateBids } from 'store/liveReferrals/liveReferralsThunks';
import {
    type NewNotification,
    receiveNewNotifications,
} from 'store/notification/notificationThunks';
import {
    setConnectionLoading,
    setSocketConnected,
    updateConversationParticipation,
    updateUserParticipation,
    type userSupportSlice,
    type UserSupportSliceState,
} from 'store/userSupport/userSupportSlice';
import {
    type SocketMessagesPayload,
    updateConversations,
    updateMessages,
} from 'store/userSupport/userSupportThunks';
import {
    type SocketPendingReferral,
    updatePendingReferrals,
} from 'store/wonReferrals/wonReferralListThunks';

interface SocketAction extends Action {
    socketType: SocketType;
    messageType: string;
    message: Record<string, unknown>;
    npi: string;
}

interface SocketMessage {
    type: string;
    message: string;
    payload: unknown;
}

interface PartialState {
    [authSlice.name]: AuthSliceState;
    [userSupportSlice.name]: UserSupportSliceState;
}

export type SocketMiddleware = MiddlewareAPI<
    ThunkDispatch<PartialState, undefined, UnknownAction>,
    PartialState
>;

interface SocketTemplate {
    store: SocketMiddleware;
    socketType: SocketType;
    socketUrl: string;
    messageMap: Record<string, (store: SocketMiddleware, message: SocketMessage) => void>;
    npi?: string | null;
    transactionId?: string | null;
}
type SocketTemplateConfig = Omit<SocketTemplate, 'store' | 'socketUrl'> & {
    store: SocketMiddleware | null;
};
type SocketMap = Record<
    SocketType,
    {
        template: SocketTemplateConfig;
        actions: {
            WS_CONNECT: (
                store: SocketMiddleware,
                action: SocketAction,
                template: SocketTemplateConfig,
            ) => SocketTemplate;
            WS_SEND?: (store: SocketMiddleware, action: SocketAction) => object;
        };
    }
>;

const wsConnected = (socketType: SocketType, host: Event) => ({
    type: 'WS_CONNECTED',
    socketType,
    host,
});
const wsDisconnected = (socketType: SocketType) => ({ type: 'WS_DISCONNECTED', socketType });
const wsReconnect = (socketType: SocketType, npi?: string | null) => ({
    type: 'WS_RECONNECT',
    socketType,
    npi,
});

interface Socket {
    isOpen: () => void;
    close: () => void;
    send: (message: string) => void;
}
const sockets: Record<SocketType, Socket | null> = {
    [SocketTypes.AUCTION]: null,
    [SocketTypes.NOTIFICATION]: null,
    [SocketTypes.PERFORMANCE]: null,
    [SocketTypes.USER_SUPPORT]: null,
};

const socketMap: SocketMap = {
    [SocketTypes.AUCTION]: {
        template: {
            store: null,
            npi: null,
            socketType: SocketTypes.AUCTION,
            messageMap: {
                STATUS: (_store, message) =>
                    console.log(`Auction WebSocket Status: ${message.message}`),
                BIDS: (store, message) =>
                    store.dispatch(
                        updateBids(message.payload as SocketBidsPayload) as ThunkAction<
                            void,
                            PartialState,
                            unknown,
                            UnknownAction
                        >,
                    ),
                AUCTIONS: (store, message) =>
                    store.dispatch(updateAuctions(message.payload as SocketLiveReferral[])),
                PENDREF: (store, message) =>
                    store.dispatch(
                        updatePendingReferrals(
                            message.payload as SocketPendingReferral[],
                        ) as ThunkAction<void, PartialState, unknown, UnknownAction>,
                    ),
            },
        },
        actions: {
            WS_CONNECT: (store, action, template) => ({
                ...template,
                store,
                npi: action.npi,
                socketUrl: getSocketURL(action.socketType),
            }),
        },
    },
    [SocketTypes.NOTIFICATION]: {
        template: {
            store: null,
            npi: null,
            socketType: SocketTypes.NOTIFICATION,
            messageMap: {
                STATUS: (_store, message) =>
                    console.log(`Notification WebSocket Status: ${message.message}`),
                NOTIFICATION: (store, message) =>
                    store.dispatch(
                        receiveNewNotifications(
                            message.payload as NewNotification[],
                        ) as ThunkAction<void, PartialState, unknown, UnknownAction>,
                    ),
            },
        },
        actions: {
            WS_CONNECT: (store, action, template) => ({
                ...template,
                store,
                npi: action.npi,
                socketUrl: getSocketURL(action.socketType),
            }),
        },
    },
    [SocketTypes.PERFORMANCE]: {
        template: {
            store: null,
            npi: null,
            socketType: SocketTypes.PERFORMANCE,
            messageMap: {
                STATUS: (_store, message) =>
                    console.log(`Performance WebSocket Status: ${message.message}`),
                CLAIMS: (store, message) =>
                    store.dispatch(updateClaimsData({ claims: message.payload as SocketClaim[] })),
            },
        },
        actions: {
            WS_CONNECT: (store, action, template) => ({
                ...template,
                store,
                npi: action.npi,
                socketUrl: getSocketURL(action.socketType),
            }),
        },
    },
    [SocketTypes.USER_SUPPORT]: {
        template: {
            store: null,
            transactionId: null,
            socketType: SocketTypes.USER_SUPPORT,
            messageMap: {
                [SocketMessageTypes.STATUS]: (_store, message) =>
                    console.log(`User Support WebSocket Status: ${message.message}`),
                [SocketMessageTypes.MESSAGES]: (store, message) => {
                    store.dispatch(
                        updateMessages(message.payload as SocketMessagesPayload) as ThunkAction<
                            void,
                            PartialState,
                            unknown,
                            UnknownAction
                        >,
                    );
                    const { canLogWebsocketPerformance } = store.getState().userSupport;
                    if (canLogWebsocketPerformance) {
                        datadogLogs.logger.info(`socket set messages for convo`, {
                            conversation_info: message.payload,
                            timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
                        });
                    }
                },
                [SocketMessageTypes.CONVERSATIONS]: (store, message) => {
                    store.dispatch(
                        updateConversations(message.payload as Conversation[]) as ThunkAction<
                            void,
                            PartialState,
                            unknown,
                            UnknownAction
                        >,
                    );
                    const { canLogWebsocketPerformance } = store.getState().userSupport;
                    if (canLogWebsocketPerformance) {
                        if (Array.isArray(message.payload) && message.payload.length > 1) {
                            datadogLogs.logger.info('socket set conversations', {
                                conversations: message.payload,
                                timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
                            });
                        } else if (Array.isArray(message.payload) && message.payload.length === 1) {
                            datadogLogs.logger.info('socket set current conversation', {
                                conversation: message.payload,
                                timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
                            });
                        }
                    }
                },
                [SocketMessageTypes.USER_PARTICIPATION]: (store, message) =>
                    store.dispatch(
                        updateUserParticipation(
                            message.payload as Record<string, string | undefined>,
                        ),
                    ),
                [SocketMessageTypes.CONVERSATION_PARTICIPATION]: (store, message) =>
                    store.dispatch(
                        updateConversationParticipation(
                            message.payload as ConversationParticipation[],
                        ),
                    ),
            },
        },
        actions: {
            WS_CONNECT: (store, action, template) => ({
                ...template,
                store,
                npi: action.npi,
                socketUrl: getSocketURL(action.socketType),
                onOpenAction: () => {
                    const {
                        userSupport: { currentReferral, currentConversation },
                    } = store.getState();
                    if (currentReferral) {
                        store.dispatch(setUserSupportReferral(currentReferral));
                        if (currentConversation) {
                            store.dispatch(setUserSupportConversation(currentConversation));
                        }
                    }
                    store.dispatch(setSocketConnected({ connected: true }));
                    store.dispatch(setConnectionLoading({ loading: false }));
                },
                onCloseAction: () => {
                    store.dispatch(setSocketConnected({ connected: false }));
                    store.dispatch(setConnectionLoading({ loading: false }));
                },
                onReconnectAttempt: () => {
                    store.dispatch(setConnectionLoading({ loading: true }));
                },
                checkIfDisconnected: () => {
                    const { userSupport } = store.getState();
                    const { socketConnected, connectionLoading } = userSupport;
                    return !socketConnected && !connectionLoading;
                },
            }),
            WS_SEND: (_store, action) => ({
                type: action.messageType,
                ...action.message,
            }),
        },
    },
};

const openSocket = ({
    store,
    npi,
    socketType,
    socketUrl,
    messageMap,
    onOpenAction,
    onCloseAction,
    onReconnectAttempt,
    checkIfDisconnected,
}: SocketTemplate & {
    onOpenAction?: () => void;
    onCloseAction?: () => void;
    onReconnectAttempt?: () => void;
    checkIfDisconnected?: () => boolean;
}): Socket => {
    let socket: WebSocket | null = null;
    let timerId: NodeJS.Timeout;
    let pingSendsCounter = 0; // "unacknowledged" ping sends

    const cancelKeepAlive = () => {
        pingSendsCounter = 0;
        if (timerId) {
            clearTimeout(timerId);
        }
    };

    const keepAlive = () => {
        const timeout = 10000;
        if (socket?.readyState === socket?.OPEN) {
            socket?.send(JSON.stringify({ type: 'KEEP-ALIVE' }));
        }
        pingSendsCounter += 1;
        if (pingSendsCounter >= 3) {
            console.warn(
                `No Server Response in ${
                    (timeout * pingSendsCounter) / 1000
                } seconds - Reconnecting ${socketType}`,
            );
            onReconnectAttempt?.();
            store.dispatch(wsReconnect(socketType, npi));
        } else if (checkIfDisconnected?.() && pingSendsCounter >= 1) {
            console.warn(`Server unexpectedly disconnected - Reconnecting ${socketType}`);
            onReconnectAttempt?.();
            store.dispatch(wsReconnect(socketType, npi));
        } else {
            timerId = setTimeout(() => keepAlive(), timeout);
        }
    };

    const isOpen = () => socket != null;

    const close = () => {
        socket?.close();
    };

    const send = (message: string) => {
        if (socket?.readyState === socket?.OPEN) {
            socket?.send(message);
        } else {
            setTimeout(() => socket?.send(message), 2000);
        }
    };

    // make sure any old sockets aren't doing any work
    cancelKeepAlive();

    // connect to the remote host
    socket = new WebSocket(`${socketUrl}/ws?npi=${npi}&user_id=${npi}`);

    // websocket handlers
    socket.onopen = (event) => {
        // authenticate
        const { canLogWebsocketPerformance } = store.getState().userSupport;
        if (canLogWebsocketPerformance) {
            datadogLogs.logger.info('socket opened', {
                timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
            });
        }
        const token = store.getState().auth.accessToken;
        const authBody = JSON.stringify({ type: 'AUTH', token });
        socket.send(authBody);

        if (canLogWebsocketPerformance) {
            datadogLogs.logger.info('socket authenticated', {
                timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
            });
        }

        console.log('websocket open', event);
        store.dispatch(wsConnected(socketType, event));

        keepAlive();

        onOpenAction?.();
    };

    socket.onclose = () => {
        console.log(`${socketType} was closed.`);
        onCloseAction?.();
        store.dispatch(wsDisconnected(socketType));
    };

    socket.onmessage = (event: { data: string }) => {
        // reset ping sends counter
        pingSendsCounter = 0;
        const { canLogWebsocketPerformance } = store.getState().userSupport;

        const message = JSON.parse(event.data) as SocketMessage;
        if (message && messageMap[message.type]) {
            if (message.type !== SocketMessageTypes.STATUS && canLogWebsocketPerformance) {
                datadogLogs.logger.info(`socket received ${message.type} message`, {
                    timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
                });
            }
            messageMap[message.type](store, message);
        } else {
            console.log('Unknown message type', message);
        }
    };

    return {
        isOpen,
        close,
        send,
    };
};

const socketMiddleware = (): ThunkMiddleware<PartialState, UnknownAction> => {
    const isSocketOpen = (socketType: SocketType) => sockets[socketType]?.isOpen();

    const openMappedSocket = (store: SocketMiddleware, action: SocketAction) => {
        sockets[action.socketType] = openSocket(
            socketMap[action.socketType].actions.WS_CONNECT(
                store,
                action,
                socketMap[action.socketType].template,
            ),
        );
    };

    const sendOnMappedSocket = (store: SocketMiddleware, action: SocketAction) => {
        console.log(
            `sending a message ${action.messageType} to socket ${action.socketType}`,
            action.message,
        );
        const { canLogWebsocketPerformance } = store.getState().userSupport;
        if (canLogWebsocketPerformance) {
            datadogLogs.logger.info(
                `${action.messageType} message sent to socket ${action.socketType}`,
                {
                    message_info: action.message,
                    timestamp_in_seconds: (dayjs().valueOf() / 1000).toString(),
                },
            );
        }
        const socketWSSend = socketMap[action.socketType].actions.WS_SEND;
        sockets[action.socketType]?.send(
            JSON.stringify(
                socketWSSend
                    ? socketWSSend(store, action)
                    : { type: action.messageType, message: action.message },
            ),
        );
    };

    const actionMap: Record<string, (store: SocketMiddleware, action: SocketAction) => void> = {
        WS_CONNECT: (store, action) => {
            if (isSocketOpen(action.socketType)) {
                sockets[action.socketType]!.close();
            }

            openMappedSocket(store, action);
        },
        WS_ENSURE_CONNECTED: (store, action) => {
            if (!isSocketOpen(action.socketType)) {
                openMappedSocket(store, action);
            }
        },
        WS_RECONNECT: (store, action) => {
            openMappedSocket(store, action);
        },
        WS_DISCONNECT: (_store, action) => {
            if (isSocketOpen(action.socketType)) {
                sockets[action.socketType]!.close();
            }
        },
        WS_SEND: (store, action) => {
            if (!isSocketOpen(action.socketType)) {
                setTimeout(() => sendOnMappedSocket(store, action), 2000);
            } else {
                sendOnMappedSocket(store, action);
            }
        },
    };

    // the middleware part of this function
    return (store) => (next) => (action) => {
        const typedAction = action as SocketAction;
        if (typedAction && actionMap[typedAction.type]) {
            return actionMap[typedAction.type](store, typedAction);
        }
        return next(action);
    };
};

export default socketMiddleware();
